import * as THREE from 'three';
import type { TimeOfDay, Weather } from '../../core/contracts';
import { glowTexture } from './textures';

export interface AtmosParams {
  skyTop: THREE.Color; skyHorizon: THREE.Color; skyGround: THREE.Color;
  sunDir: THREE.Vector3; sunColor: THREE.Color; sunIntensity: number; sunDisk: number;
  hemiSky: THREE.Color; hemiGround: THREE.Color; hemiIntensity: number;
  fogColor: THREE.Color; fogDensity: number;
  stars: number; clouds: number; cloudColor: THREE.Color;
  wet: number; snow: number;
  grassLight: THREE.Color; grassDark: THREE.Color;
  blobOpacity: number; shadowRadius: number; floodlights: boolean;
  /** 0..1 darkness of cast shadows (soft/flat light → low). */
  shadowIntensity: number;
  /** Shadow stretch direction/length from the sun (0 length = round). */
  blobStretch: number;
}

const C = (h: string) => new THREE.Color(h);
const dirFromAngles = (elevDeg: number, azDeg: number) => {
  const e = THREE.MathUtils.degToRad(elevDeg), a = THREE.MathUtils.degToRad(azDeg);
  // azimuth 0 = from +x, 90 = from +z
  return new THREE.Vector3(Math.cos(e) * Math.cos(a), Math.sin(e), Math.cos(e) * Math.sin(a)).normalize();
};

export function atmosParams(time: TimeOfDay, weather: Weather): AtmosParams {
  let p: AtmosParams;
  if (time === 'afternoon') {
    p = {
      skyTop: C('#2f6fc4'), skyHorizon: C('#b8d4ee'), skyGround: C('#6d7a6a'),
      sunDir: dirFromAngles(50, -115), sunColor: C('#fff3e0'), sunIntensity: 3.1, sunDisk: 1,
      hemiSky: C('#c4dcff'), hemiGround: C('#5b7a45'), hemiIntensity: 1.25,
      fogColor: C('#b9cde0'), fogDensity: 0.0016,
      stars: 0, clouds: 0.45, cloudColor: C('#ffffff'),
      wet: 0, snow: 0, grassLight: C('#56a238'), grassDark: C('#327a26'),
      blobOpacity: 0.85, shadowRadius: 2, floodlights: false, blobStretch: 1, shadowIntensity: 0.92,
    };
  } else if (time === 'golden_hour') {
    p = {
      skyTop: C('#3d5f9c'), skyHorizon: C('#ffb37a'), skyGround: C('#5a4a3a'),
      sunDir: dirFromAngles(21, 160), sunColor: C('#ffad62'), sunIntensity: 3.4, sunDisk: 1.4,
      hemiSky: C('#a9bde6'), hemiGround: C('#6b5a3a'), hemiIntensity: 0.95,
      fogColor: C('#e9b98e'), fogDensity: 0.0022,
      stars: 0, clouds: 0.35, cloudColor: C('#ffd2a8'),
      wet: 0, snow: 0, grassLight: C('#5aa23a'), grassDark: C('#347826'),
      blobOpacity: 0.8, shadowRadius: 2, floodlights: false, blobStretch: 1, shadowIntensity: 0.9,
    };
  } else {
    p = {
      skyTop: C('#03060d'), skyHorizon: C('#1a2335'), skyGround: C('#0a0c10'),
      sunDir: dirFromAngles(72, -100), sunColor: C('#f1f5ff'), sunIntensity: 2.9, sunDisk: 0,
      hemiSky: C('#7b8cb5'), hemiGround: C('#2c4a22'), hemiIntensity: 0.75,
      fogColor: C('#121a28'), fogDensity: 0.0018,
      stars: 1, clouds: 0.15, cloudColor: C('#202838'),
      wet: 0, snow: 0, grassLight: C('#4f9d36'), grassDark: C('#2d7524'),
      blobOpacity: 0.9, shadowRadius: 3, floodlights: true, blobStretch: 0, shadowIntensity: 0.6,
    };
  }
  const grey = (amt: number, col: string) => {
    p.skyTop.lerp(C(col), amt); p.skyHorizon.lerp(C(col).multiplyScalar(1.15), amt);
    p.sunIntensity *= 1 - amt * 0.7; p.hemiIntensity *= 1 + amt * 0.5;
    p.sunDisk *= 1 - amt; p.clouds = Math.max(p.clouds, amt);
    p.cloudColor.lerp(C(col), amt * 0.6);
    p.fogColor.lerp(C(col), amt);
    p.shadowRadius += amt * 6; p.blobOpacity *= 1 - amt * 0.3;
    p.shadowIntensity *= 1 - amt * 0.75;
    if (amt > 0.6) p.blobStretch = 0;
    p.stars *= 1 - amt;
  };
  const night = time === 'night';
  switch (weather) {
    case 'clear': break;
    case 'overcast': grey(0.8, night ? '#1d2230' : '#8f99a6'); break;
    case 'rain':
      grey(0.92, night ? '#161b26' : '#66707c');
      p.wet = 1; p.fogDensity = 0.0055; p.sunIntensity *= night ? 1.3 : 0.9;
      break;
    case 'snow':
      grey(0.9, night ? '#262c38' : '#b3bac3');
      p.snow = 1; p.fogDensity = 0.0034; p.hemiGround = C('#c8ced6');
      break;
    case 'fog':
      grey(0.85, night ? '#2a303a' : '#b8bec6');
      p.fogDensity = 0.016; p.fogColor = night ? C('#3a414d') : C('#c3c8cf');
      p.skyTop.copy(p.fogColor); p.skyHorizon.copy(p.fogColor);
      break;
  }
  if (night) { p.sunIntensity = Math.max(p.sunIntensity, 2.4); p.sunDisk = 0; }
  return p;
}

/**
 * Program-feed look per time of day / weather (consumed by the multiview renderer as
 * `scene.userData.sdPost`). Values are deliberately subtle: a broadcast grade, not a filter.
 */
export function broadcastPost(time: TimeOfDay, weather: Weather): { bloom: number; bloomThreshold: number; bloomKnee: number; saturation: number; contrast: number; tint: [number, number, number] } {
  const grey = weather === 'overcast' || weather === 'rain' || weather === 'fog' || weather === 'snow';
  if (time === 'night') {
    return { bloom: grey ? 0.75 : 0.6, bloomThreshold: 1.05, bloomKnee: 0.45, saturation: 1.1, contrast: 1.07, tint: [0.98, 1.0, 1.03] };
  }
  if (time === 'golden_hour') {
    return { bloom: 0.3, bloomThreshold: 1.6, bloomKnee: 0.6, saturation: grey ? 1.04 : 1.08, contrast: 1.05, tint: [1.03, 1.0, 0.95] };
  }
  return { bloom: 0.18, bloomThreshold: 2.0, bloomKnee: 0.6, saturation: grey ? 1.06 : 1.1, contrast: 1.06, tint: [1, 1, 1] };
}

// --------------------------------------------------------------------- sky
const SKY_VERT = /* glsl */`
varying vec3 vDir;
void main() {
  vDir = position;
  vec4 p = projectionMatrix * viewMatrix * vec4(position + cameraPosition, 1.0);
  gl_Position = p.xyww;
}`;

const SKY_FRAG = /* glsl */`
uniform vec3 uTop; uniform vec3 uHorizon; uniform vec3 uGround;
uniform vec3 uSunDir; uniform vec3 uSunColor; uniform float uSunDisk;
uniform float uStars; uniform float uClouds; uniform vec3 uCloudColor; uniform float uTime;
varying vec3 vDir;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
}
void main() {
  vec3 d = normalize(vDir);
  float h = d.y;
  vec3 col = mix(uHorizon, uTop, pow(clamp(h, 0.0, 1.0), 0.55));
  col = mix(col, uGround, smoothstep(0.0, -0.08, h));
  float sd = max(dot(d, uSunDir), 0.0);
  col += uSunColor * uSunDisk * (pow(sd, 900.0) * 30.0 + pow(sd, 24.0) * 0.35 + pow(sd, 4.0) * 0.12);
  if (h > 0.0) {
    vec2 uv = d.xz / (h + 0.12) * 1.2 + vec2(uTime * 0.004, uTime * 0.0015);
    float n = noise(uv * 1.3) * 0.55 + noise(uv * 3.1) * 0.3 + noise(uv * 7.7) * 0.15;
    float c = smoothstep(0.62 - uClouds * 0.35, 0.95 - uClouds * 0.2, n) * smoothstep(0.0, 0.25, h);
    col = mix(col, uCloudColor * (0.75 + 0.35 * n) + uSunColor * uSunDisk * pow(sd, 6.0) * 0.4, c * min(1.0, uClouds * 1.5));
    if (uStars > 0.0) {
      vec2 sp = d.xz / (h + 1.0) * 420.0;
      vec2 cell = floor(sp);
      float r = hash(cell);
      float star = step(0.985, r) * smoothstep(0.35, 0.0, length(fract(sp) - 0.5)) * smoothstep(0.05, 0.4, h);
      col += vec3(star) * uStars * (0.4 + 0.6 * hash(cell + 3.1)) * (1.0 - c);
    }
  }
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export function buildSky(): { mesh: THREE.Mesh; uniforms: Record<string, THREE.IUniform> } {
  const uniforms = {
    uTop: { value: new THREE.Color() }, uHorizon: { value: new THREE.Color() }, uGround: { value: new THREE.Color() },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) }, uSunColor: { value: new THREE.Color() }, uSunDisk: { value: 1 },
    uStars: { value: 0 }, uClouds: { value: 0 }, uCloudColor: { value: new THREE.Color() }, uTime: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms, vertexShader: SKY_VERT, fragmentShader: SKY_FRAG,
    side: THREE.BackSide, depthWrite: false, depthFunc: THREE.LessEqualDepth, fog: false,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(10, 32, 16), mat);
  mesh.name = 'sky';
  mesh.frustumCulled = false;
  mesh.renderOrder = -1000;
  mesh.matrixAutoUpdate = false;
  return { mesh, uniforms };
}

// --------------------------------------------------------------------- billboards (glow + snow)
const BILL_VERT = /* glsl */`
attribute vec4 aOffset; // xyz + size
uniform float uTime; uniform float uMode; // 0 glow, 1 snow
uniform vec3 uBox;
varying vec2 vUv; varying float vFade;
void main() {
  vUv = uv;
  vec3 c = aOffset.xyz;
  float size = aOffset.w;
  vFade = 1.0;
  if (uMode > 0.5) {
    // aOffset.xyz are seeds in [0,1]
    float H = uBox.y;
    float y = H - mod(c.y * H + uTime * (0.8 + c.x * 0.6), H);
    float sway = sin(uTime * (0.6 + c.z) + c.x * 40.0) * 0.6;
    c = vec3((c.x * 2.0 - 1.0) * uBox.x + sway, y, (c.z * 2.0 - 1.0) * uBox.z + cos(uTime * 0.5 + c.z * 30.0) * 0.5);
    vFade = smoothstep(0.0, 3.0, y) * smoothstep(H, H - 4.0, y);
  }
  vec4 mv = viewMatrix * vec4(c, 1.0);
  mv.xy += position.xy * size;
  gl_Position = projectionMatrix * mv;
}`;
const BILL_FRAG = /* glsl */`
uniform sampler2D uMap; uniform vec3 uColor; uniform float uOpacity; uniform float uMode;
varying vec2 vUv; varying float vFade;
void main() {
  float a;
  if (uMode > 0.5) { float r = length(vUv - 0.5) * 2.0; a = smoothstep(1.0, 0.3, r); }
  else a = texture2D(uMap, vUv).a;
  gl_FragColor = vec4(uColor, a * uOpacity * vFade);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

function billboardMesh(data: Float32Array, count: number, mode: 0 | 1, color: THREE.Color, opacity: number, blending: THREE.Blending, box: THREE.Vector3) {
  const base = new THREE.PlaneGeometry(1, 1);
  const g = new THREE.InstancedBufferGeometry();
  g.index = base.index;
  g.setAttribute('position', base.attributes.position!);
  g.setAttribute('uv', base.attributes.uv!);
  g.setAttribute('aOffset', new THREE.InstancedBufferAttribute(data, 4));
  g.instanceCount = count;
  const uniforms = {
    uTime: { value: 0 }, uMode: { value: mode }, uBox: { value: box },
    uMap: { value: mode === 0 ? glowTexture() : null }, uColor: { value: color }, uOpacity: { value: opacity },
  };
  const mat = new THREE.ShaderMaterial({ uniforms, vertexShader: BILL_VERT, fragmentShader: BILL_FRAG, transparent: true, depthWrite: false, blending, fog: false });
  const mesh = new THREE.Mesh(g, mat);
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  return { mesh, uniforms, geometry: g };
}

export function buildGlows(lamps: number[]) {
  const n = lamps.length / 4;
  const data = new Float32Array(lamps);
  const b = billboardMesh(data, n, 0, new THREE.Color(1.6, 1.55, 1.4), 1, THREE.AdditiveBlending, new THREE.Vector3());
  b.mesh.name = 'floodlight-glow';
  b.mesh.renderOrder = 5;
  return b;
}

export function buildSnow(max: number) {
  const data = new Float32Array(max * 4);
  for (let i = 0; i < max; i++) {
    data[i * 4] = Math.random(); data[i * 4 + 1] = Math.random(); data[i * 4 + 2] = Math.random(); data[i * 4 + 3] = 0.05 + Math.random() * 0.05;
  }
  const b = billboardMesh(data, max, 1, new THREE.Color(1, 1, 1), 0.9, THREE.NormalBlending, new THREE.Vector3(85, 45, 70));
  b.mesh.name = 'snow';
  b.mesh.renderOrder = 6;
  return b;
}

// --------------------------------------------------------------------- rain
const RAIN_VERT = /* glsl */`
attribute float aEnd;
uniform float uTime; uniform vec3 uBox; uniform vec3 uVel; uniform float uLen;
varying float vA;
void main() {
  vec3 s = position;
  float H = uBox.y;
  float y = H - mod(s.y * H + uTime * -uVel.y, H);
  vec3 p = vec3((s.x * 2.0 - 1.0) * uBox.x, y, (s.z * 2.0 - 1.0) * uBox.z);
  p.xz += uVel.xz * (H - y) / -uVel.y;
  vec3 dir = normalize(uVel);
  p -= dir * uLen * aEnd;
  vA = (1.0 - aEnd * 0.85) * smoothstep(0.0, 2.0, y);
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}`;
const RAIN_FRAG = /* glsl */`
uniform vec3 uColor; uniform float uOpacity; varying float vA;
void main() {
  gl_FragColor = vec4(uColor, uOpacity * vA);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export function buildRain(max: number) {
  const pos = new Float32Array(max * 2 * 3);
  const end = new Float32Array(max * 2);
  for (let i = 0; i < max; i++) {
    const x = Math.random(), y = Math.random(), z = Math.random();
    for (let k = 0; k < 2; k++) { const o = (i * 2 + k) * 3; pos[o] = x; pos[o + 1] = y; pos[o + 2] = z; end[i * 2 + k] = k; }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aEnd', new THREE.BufferAttribute(end, 1));
  const uniforms = {
    uTime: { value: 0 }, uBox: { value: new THREE.Vector3(85, 45, 72) }, uVel: { value: new THREE.Vector3(1.2, -14, 0.6) },
    uLen: { value: 0.9 }, uColor: { value: new THREE.Color(0.75, 0.8, 0.88) }, uOpacity: { value: 0.32 },
  };
  const mat = new THREE.ShaderMaterial({ uniforms, vertexShader: RAIN_VERT, fragmentShader: RAIN_FRAG, transparent: true, depthWrite: false, fog: false });
  const mesh = new THREE.LineSegments(g, mat);
  mesh.name = 'rain';
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.renderOrder = 6;
  return { mesh, uniforms, geometry: g, max };
}
